import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { Select, Grid } from "@mantine/core";
import { useForm } from "@mantine/form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/sharp-regular-svg-icons";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { FeatureToggle } from "shared/src/constants/featureToggleNames";
import { useProviderName } from "@/hooks/useProviderName";
import {
  OPTUM_FRONTIER_THERAPIES,
  PANTHERX,
} from "shared/src/data/providerNames";
import { gql } from "@/__generated__";
import Navbar from "@/components/layout/Navbar";
import InlineBanner from "@/components/shared/InlineBanner";
import { PageFooter } from "@/components/layout/PageFooter";
import {
  InitializeReferralInput,
  SubDenialReason,
  OtherAppealSituations,
} from "@/__generated__/graphql";
import {
  Condition,
  getConditionsTreatedByMedication,
  getMedicationsForReferral,
  Medication,
} from "shared/src/data/medication-condition-pairs";
import { DenialReason } from "@/data/denial-reasons";
import { shouldShowSpecialSituationsField } from "shared/src/utils/specialSituationsUtils";
import { SpecialAppealSituationsCard } from "./SpecialAppealSituationsCard";
import { SubDenialReasonField } from "./SubDenialReasonField";

import styles from "./initial-referral-page.module.scss";
import "../../styles/components/landing-page.scss";
import { ReferPatientProgressTracker } from "@/components/ReferPatientProgressTracker";

const INITIALIZE_REFERRAL = gql(`
  mutation InitializeReferral($input: InitializeReferralInput!) {
    initializeReferral(input: $input) {
      appealId
      condition
      medication
      denialReason
    }
  }
`);

interface FormValues {
  condition: string;
  medication: string;
  denialReason: string;
  subDenialReasons: SubDenialReason[];
}

export function InitialReferralPage() {
  const navigate = useNavigate();

  const providerName = useProviderName();
  const isCsidSucraidEnabled =
    providerName?.includes(OPTUM_FRONTIER_THERAPIES) ?? false;
  const isHppStrensiqEnabled = providerName?.includes(PANTHERX) ?? false;
  const isLiverTransplantEnabled =
    useFeatureFlagEnabled(FeatureToggle.LIVER_TRANSPLANT) === true;

  const [selectedSituations, setSelectedSituations] = useState<
    OtherAppealSituations[]
  >([]);

  const form = useForm<FormValues>({
    initialValues: {
      condition: "",
      medication: "",
      denialReason: "",
      subDenialReasons: [],
    },
    validate: {
      condition: (value) => (!value ? "Medical condition is required" : null),
      medication: (value) => (!value ? "Medication is required" : null),
      denialReason: (value) => (!value ? "Denial reason is required" : null),
    },
  });

  const medication = form.values.medication;
  const condition = form.values.condition;

  useEffect(() => {
    if (condition && medication) {
      const availableConditions = getConditionsTreatedByMedication(
        medication as Medication,
      );
      if (!availableConditions.includes(condition)) {
        form.setFieldValue("condition", "");
      }
    }
  }, [medication]);

  const denialReason = form.values.denialReason;
  const hppDenialReasons: string[] = [
    DenialReason.MEDICAL_NECESSITY,
    DenialReason.FORMULARY,
  ];

  useEffect(() => {
    if (
      condition === Condition.HYPOPHOSPHATASIA &&
      denialReason &&
      !hppDenialReasons.includes(denialReason)
    ) {
      form.setFieldValue("denialReason", "");
    }
  }, [condition]);

  const shouldShowSpecialSituations = shouldShowSpecialSituationsField(
    medication,
    condition,
  );

  useEffect(() => {
    if (!shouldShowSpecialSituations) {
      setSelectedSituations([]);
    }
  }, [shouldShowSpecialSituations]);

  const medicationOptions = isHppStrensiqEnabled
    ? [Medication.STRENSIQ]
    : getMedicationsForReferral({
        conditions: {
          [Condition.PANDAS]: false,
          [Condition.PANS]: false,
          [Condition.CSID]: isCsidSucraidEnabled,
          [Condition.HYPOPHOSPHATASIA]: false,
          [Condition.ATOPIC_DERMATITIS]: false,
          [Condition.CHRONIC_RHINOSINUSITIS]: false,
          [Condition.EOSINOPHILIC_ESOPHAGITIS]: false,
          [Condition.PRURIGO_NODULARIS]: false,
          [Condition.COPD]: false,
          [Condition.CHRONIC_SPONTANEOUS_URTICARIA]: false,
          [Condition.BULLOUS_PEMPHIGOID]: false,
          [Condition.LIVER_CANCER]: isLiverTransplantEnabled,
        },
      });

  const [initializeReferral, { loading, error: initializeError }] =
    useMutation(INITIALIZE_REFERRAL);

  const toggleSituation = (situation: OtherAppealSituations) => {
    setSelectedSituations((prev) =>
      prev.includes(situation)
        ? prev.filter((s) => s !== situation)
        : [...prev, situation],
    );
  };

  const handleSubmit = async (values: FormValues) => {
    const input: InitializeReferralInput = {
      condition: values.condition,
      medication: values.medication,
      denialReason: values.denialReason,
      subDenialReasons:
        values.subDenialReasons.length > 0
          ? values.subDenialReasons
          : undefined,
      otherAppealSituations:
        selectedSituations.length > 0 ? selectedSituations : undefined,
    };

    try {
      const { data } = await initializeReferral({ variables: { input } });
      if (data?.initializeReferral) {
        const { appealId } = data.initializeReferral;
        navigate(`/refer/${appealId}/patient-info`);
      }
    } catch {}
  };

  const handleCancel = () => {
    navigate("/");
  };

  const isFormValid = () => {
    const { condition, medication, denialReason } = form.values;
    return condition && medication && denialReason;
  };

  const handleFooterSubmit = async () => {
    const validationResult = form.validate();
    if (validationResult.hasErrors) {
      return;
    }
    await handleSubmit(form.getValues());
  };

  const footer = (
    <PageFooter
      secondaryButton={{
        text: "Cancel",
        onClick: handleCancel,
      }}
      primaryButton={{
        text: "Next",
        onClick: handleFooterSubmit,
        disabled: loading || !isFormValid(),
        loading,
      }}
    />
  );

  return (
    <Navbar titleComponent={<ReferPatientProgressTracker />} footer={footer}>
      <div className={styles.pageContainer}>
        <div className={styles.formContainer}>
          <h2 className={styles.formHeader}>Denial Info</h2>
          <div className={styles.formSubheader}>
            <p className={styles.formSubheader__text}>
              Tell us about the denial your patient needs to appeal. Don't see
              what you're looking for? <a href="#">Click here.</a>
            </p>
          </div>
          <hr className={styles.sectionDivider} />

          {initializeError && (
            <>
              <InlineBanner
                type="error"
                message={initializeError.message}
                key={initializeError.message}
              />
              <br />
            </>
          )}

          <form
            id="initial-referral-form"
            onSubmit={form.onSubmit(handleSubmit)}
          >
            <Grid>
              <Grid.Col>
                <Select
                  label="Medication"
                  placeholder="Look up by name"
                  data-testid="medication-input"
                  searchable
                  withAsterisk
                  disabled={loading}
                  error={form.errors.medication}
                  data={medicationOptions}
                  rightSection={
                    <FontAwesomeIcon icon={faMagnifyingGlass} size="lg" />
                  }
                  {...form.getInputProps("medication")}
                />
              </Grid.Col>
            </Grid>

            <Grid mt="md">
              <Grid.Col>
                <Select
                  label="Condition"
                  placeholder="Look up by condition"
                  data-testid="condition-input"
                  searchable
                  withAsterisk
                  disabled={loading || !medication}
                  error={form.errors.condition}
                  data={
                    medication
                      ? getConditionsTreatedByMedication(
                          medication as Medication,
                        )
                      : []
                  }
                  rightSection={
                    <FontAwesomeIcon icon={faMagnifyingGlass} size="lg" />
                  }
                  {...form.getInputProps("condition")}
                />
              </Grid.Col>
            </Grid>

            <Grid mt="md">
              <Grid.Col>
                <Select
                  label="Denial Reason"
                  placeholder="Select denial reason"
                  data-testid="denial-reason-input"
                  withAsterisk
                  disabled={loading}
                  error={form.errors.denialReason}
                  data={
                    condition === Condition.HYPOPHOSPHATASIA
                      ? hppDenialReasons
                      : Object.values(DenialReason)
                  }
                  {...form.getInputProps("denialReason")}
                />
              </Grid.Col>
            </Grid>

            <SubDenialReasonField
              condition={form.values.condition}
              denialReason={form.values.denialReason}
              subDenialReasons={form.values.subDenialReasons}
              disabled={loading}
              onChange={(values) =>
                form.setFieldValue("subDenialReasons", values)
              }
              onClear={() => form.setFieldValue("subDenialReasons", [])}
              error={form.errors.subDenialReasons}
            />
          </form>
        </div>

        {shouldShowSpecialSituations && (
          <div className={styles.rightColumnContainer}>
            <SpecialAppealSituationsCard
              selectedSituations={selectedSituations}
              onToggleSituation={toggleSituation}
              disabled={loading}
            />
          </div>
        )}
      </div>
    </Navbar>
  );
}
